import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  FormGroup,
  Label,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Spinner,
  Alert,
  Pagination,
  PaginationItem,
  PaginationLink,
  UncontrolledTooltip,
} from 'reactstrap';
import { ParametrosTintaExpanded, Tinta } from '../types/collections';

interface InkSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedParametros: ParametrosTintaExpanded[]) => void;
  selectedParametros: ParametrosTintaExpanded[];
  parametros: ParametrosTintaExpanded[];
  loading?: boolean;
  error?: string | null;
  onReload?: () => void;
}

interface FilteredParametros {
  parametros: ParametrosTintaExpanded[];
  totalPages: number;
  currentPage: number;
}

const ITEMS_PER_PAGE = 10;

export const InkSelectionModal: React.FC<InkSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  selectedParametros,
  parametros,
  loading = false,
  error = null,
  onReload,
}) => {
  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInkType, setSelectedInkType] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [localSelectedParametros, setLocalSelectedParametros] = useState<ParametrosTintaExpanded[]>([]);

  // Get unique ink types for filter
  const inkTypes = useMemo(() => {
    const types = new Set<string>();
    parametros.forEach(p => {
      if (p.expand?.tinta && Array.isArray(p.expand.tinta)) {
        p.expand.tinta.forEach(tinta => {
          if (tinta.nombre) {
            types.add(tinta.nombre);
          }
        });
      }
    });
    return Array.from(types).sort();
  }, [parametros]);

  // Filter and paginate parameters
  const filteredParametros: FilteredParametros = useMemo(() => {
    let filtered = parametros;

    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => {
        const hasMatchingTinta = p.expand?.tinta && Array.isArray(p.expand.tinta) 
          ? p.expand.tinta.some(tinta => tinta.nombre?.toLowerCase().includes(term))
          : false;
        return p.nombre.toLowerCase().includes(term) ||
               p.pantone.toLowerCase().includes(term) ||
               hasMatchingTinta;
      });
    }

    // Apply ink type filter
    if (selectedInkType !== 'all') {
      filtered = filtered.filter(p => {
        return p.expand?.tinta && Array.isArray(p.expand.tinta) 
          ? p.expand.tinta.some(tinta => tinta.nombre === selectedInkType)
          : false;
      });
    }

    // Calculate pagination
    const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginatedParametros = filtered.slice(startIndex, endIndex);

    return {
      parametros: paginatedParametros,
      totalPages,
      currentPage: Math.min(currentPage, totalPages || 1),
    };
  }, [parametros, searchTerm, selectedInkType, currentPage]);

  // Initialize local selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalSelectedParametros(selectedParametros);
    }
  }, [isOpen, selectedParametros]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setSelectedInkType('all');
      setCurrentPage(1);
      setLocalSelectedParametros([]);
    }
  }, [isOpen]);

  // Handle parameter selection
  const handleParameterToggle = (parametro: ParametrosTintaExpanded) => {
    setLocalSelectedParametros(prev => {
      const isSelected = prev.some(p => p.id === parametro.id);
      if (isSelected) {
        return prev.filter(p => p.id !== parametro.id);
      } else {
        return [...prev, parametro];
      }
    });
  };

  // Handle select all on current page
  const handleSelectAll = () => {
    const allCurrentPageSelected = filteredParametros.parametros.every(p => 
      localSelectedParametros.some(selected => selected.id === p.id)
    );

    if (allCurrentPageSelected) {
      // Deselect all on current page
      setLocalSelectedParametros(prev => 
        prev.filter(selected => 
          !filteredParametros.parametros.some(p => p.id === selected.id)
        )
      );
    } else {
      // Select all on current page
      const newSelections = filteredParametros.parametros.filter(p => 
        !localSelectedParametros.some(selected => selected.id === p.id)
      );
      setLocalSelectedParametros(prev => [...prev, ...newSelections]);
    }
  };

  // Handle search
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Handle ink type filter
  const handleInkTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedInkType(e.target.value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle confirm
  const handleConfirm = () => {
    onConfirm(localSelectedParametros);
  };

  // Check if parameter is selected
  const isParameterSelected = (parametro: ParametrosTintaExpanded) => {
    return localSelectedParametros.some(p => p.id === parametro.id);
  };

  // Get color preview style
  const getColorPreviewStyle = (pantone: string) => {
    // This is a simplified color mapping - in a real app, you'd have a proper Pantone color database
    const colorMap: { [key: string]: string } = {
      '3005 C': '#009639',
      '186 C': '#C8102E',
      '109 C': '#FFD100',
      '286 C': '#003DA5',
      '355 C': '#E4002B',
      '347 C': '#00B04F',
    };
    
    return {
      backgroundColor: colorMap[pantone] || '#CCCCCC',
      width: '24px',
      height: '24px',
      borderRadius: '4px',
      border: '1px solid #ddd',
      display: 'inline-block',
    };
  };

  // Check if all current page items are selected
  const allCurrentPageSelected = filteredParametros.parametros.length > 0 && 
    filteredParametros.parametros.every(p => isParameterSelected(p));

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" className="ink-selection-modal" data-testid="ink-selection-modal">
      <ModalHeader toggle={onClose}>
        <div className="modal-title">
          <i className="fas fa-palette me-2" aria-hidden="true"></i>
          Seleccionar Parámetros de Tinta
        </div>
      </ModalHeader>
      
      <ModalBody>
        <div id="ink-selection-description" className="sr-only">
          Selecciona los parámetros de tinta que deseas incluir en la orden de producción.
        </div>

        {/* Search and Filter Controls */}
        <Row className="mb-3">
          <Col md={8}>
            <FormGroup>
              <Label for="search-input" className="visually-hidden">
                Buscar parámetros de tinta
              </Label>
              <div className="position-relative">
                <Input
                  id="search-input"
                  type="text"
                  placeholder="Buscar parámetros..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="form-control"
                  aria-label="Buscar parámetros de tinta"
                />
                {searchTerm && (
                  <Button
                    color="link"
                    size="sm"
                    className="position-absolute end-0 top-50 translate-middle-y me-2"
                    onClick={handleClearSearch}
                    aria-label="Limpiar búsqueda"
                  >
                    <i className="fas fa-times"></i>
                  </Button>
                )}
              </div>
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label for="ink-type-filter" className="visually-hidden">
                Filtrar por tipo de tinta
              </Label>
              <Input
                id="ink-type-filter"
                type="select"
                value={selectedInkType}
                onChange={handleInkTypeChange}
                aria-label="Filtrar por tipo de tinta"
              >
                <option value="all">Todos los tipos</option>
                {inkTypes.map((type: string) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        </Row>

        {/* Selection Summary */}
        {localSelectedParametros.length > 0 && (
          <Alert color="info" className="mb-3" fade={true} timeout={150}>
            <i className="fas fa-info-circle me-2"></i>
            {localSelectedParametros.length} parámetro{localSelectedParametros.length !== 1 ? 's' : ''} seleccionado{localSelectedParametros.length !== 1 ? 's' : ''}
          </Alert>
        )}

        {/* Error Display */}
        {error && (
          <Alert color="danger" className="mb-3" fade={true} timeout={150}>
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {error}
              </div>
              {onReload && (
                <Button
                  color="outline-danger"
                  size="sm"
                  onClick={onReload}
                  disabled={loading}
                  title="Recargar parámetros de tinta"
                >
                  <i className="fas fa-sync-alt me-1"></i>
                  Recargar
                </Button>
              )}
            </div>
          </Alert>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2 mb-0">Cargando parámetros...</p>
          </div>
        ) : (
          <>
            {/* Select All Controls */}
            {filteredParametros.parametros.length > 0 && (
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="form-check">
                  <Input
                    type="checkbox"
                    id="select-all"
                    checked={allCurrentPageSelected}
                    onChange={handleSelectAll}
                    className="form-check-input"
                  />
                  <Label for="select-all" className="form-check-label">
                    Seleccionar todos en esta página
                  </Label>
                </div>
                <Badge color="secondary">
                  {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, parametros.length)} de {parametros.length} parámetros
                </Badge>
              </div>
            )}

            {/* Parameters List */}
            {filteredParametros.parametros.length === 0 ? (
              <div className="text-center py-4">
                <i className="fas fa-search fa-3x text-muted mb-3"></i>
                <p className="text-muted">No se encontraron parámetros</p>
                {searchTerm && (
                  <Button color="link" onClick={handleClearSearch}>
                    Limpiar filtros
                  </Button>
                )}
              </div>
            ) : (
              <div className="parameters-list">
                {filteredParametros.parametros.map((parametro) => (
                  <Card key={parametro.id} className="mb-2 parameter-item">
                    <CardBody className="py-2">
                      <div className="d-flex align-items-center">
                        <div className="form-check me-3">
                          <Input
                            type="checkbox"
                            id={`parametro-${parametro.id}`}
                            checked={isParameterSelected(parametro)}
                            onChange={() => handleParameterToggle(parametro)}
                            className="form-check-input"
                          />
                          <Label for={`parametro-${parametro.id}`} className="form-check-label visually-hidden">
                            Seleccionar {parametro.nombre}
                          </Label>
                        </div>
                        
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center">
                            <div
                              data-testid="color-preview"
                              style={getColorPreviewStyle(parametro.pantone)}
                              className="me-3"
                              title={`Color Pantone: ${parametro.pantone}`}
                            ></div>
                            <div>
                              <h6 className="mb-1">{parametro.nombre}</h6>
                              <div className="text-muted small">
                                <span className="me-2">
                                  <i className="fas fa-tint me-1"></i>
                                  {parametro.expand?.tinta && Array.isArray(parametro.expand.tinta) 
                                    ? (() => {
                                        const order = ['C', 'M', 'Y', 'K'];
                                        return parametro.expand.tinta
                                          .sort((a, b) => {
                                            const aIndex = order.indexOf(a.nombre);
                                            const bIndex = order.indexOf(b.nombre);
                                            
                                            if (aIndex !== -1 && bIndex !== -1) {
                                              return aIndex - bIndex;
                                            }
                                            if (aIndex !== -1) return -1;
                                            if (bIndex !== -1) return 1;
                                            return a.nombre.localeCompare(b.nombre);
                                          })
                                          .map(tinta => tinta.nombre)
                                          .join(', ');
                                      })()
                                    : 'Sin tipo'}
                                </span>
                                <span>
                                  <i className="fas fa-palette me-1"></i>
                                  {parametro.pantone}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </div>
            )}

            {/* Pagination */}
            {filteredParametros.totalPages > 1 && (
              <div className="d-flex justify-content-center mt-3">
                <Pagination>
                  <PaginationItem disabled={currentPage === 1}>
                    <PaginationLink
                      previous
                      onClick={() => handlePageChange(currentPage - 1)}
                      aria-label="Página anterior"
                    />
                  </PaginationItem>
                  
                  {Array.from({ length: filteredParametros.totalPages }, (_, i) => i + 1).map(page => (
                    <PaginationItem key={page} active={page === currentPage}>
                      <PaginationLink onClick={() => handlePageChange(page)}>
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  
                  <PaginationItem disabled={currentPage === filteredParametros.totalPages}>
                    <PaginationLink
                      next
                      onClick={() => handlePageChange(currentPage + 1)}
                      aria-label="Página siguiente"
                    />
                  </PaginationItem>
                </Pagination>
              </div>
            )}
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={onClose}>
          <i className="fas fa-times me-2"></i>
          Cancelar
        </Button>
        <Button
          color="primary"
          onClick={handleConfirm}
          disabled={loading || localSelectedParametros.length === 0}
          style={{ opacity: (loading || localSelectedParametros.length === 0) ? 0.65 : 1 }}
          title={localSelectedParametros.length === 0 ? 'Selecciona al menos un parámetro' : 'Confirmar'}
        >
          <i className="fas fa-check me-2"></i>
          Confirmar{localSelectedParametros.length > 0 ? ` (${localSelectedParametros.length})` : ''}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
